import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import { PARTICLE_CAP } from "../shared/presentation";
import type { TargetRangeSpec } from "./spec";

/**
 * Shared layout constants: both the evaluator's physics and the compiler's
 * rendering must agree on exactly where the defender/protectee line sits,
 * or the proof and the compiled game would be playing different geometry.
 */
export const RANGE_GEOMETRY = {
  /** Defender stands this far above the range's bottom edge. */
  defenderYInset: 56,
  /** Protectee row sits this far above the bottom edge. */
  protecteeYInset: 16,
  /** Fixed protectee/lives count -- losing all of them ends the round. */
  protecteeCount: 3,
  /** Attackers spawn within this fraction of the range's height from the top. */
  spawnTopBand: 0.22,
} as const;

export interface SpawnedAttacker {
  readonly index: number;
  readonly wave: number;
  readonly spawnSeconds: number;
  /** Seconds the attacker takes to travel from its spawn point to the defender if never intercepted. */
  readonly totalTimeSeconds: number;
  readonly x: number;
  readonly y: number;
}

/**
 * The exact seeded spawn schedule both the evaluator and the compiled game
 * share (embedded as data, never re-derived). Attackers spawn in a band
 * near the top of the range and fly in a straight line toward the fixed
 * defender point (see `defenderPoint`). With waves, the schedule splits
 * into escalating segments: later waves spawn denser and travel faster
 * (15% shorter travel time per wave, floored so the interception proof
 * below can still pass for a validator-range spec).
 */
export function spawnSchedule(spec: TargetRangeSpec): readonly SpawnedAttacker[] {
  const { rangeWidth, rangeHeight, targetCount, targetRadius, targetLifetimeSeconds, timeLimitSeconds } = spec.params;
  const waves = spec.params.waves ?? 1;
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
  const attackers: SpawnedAttacker[] = [];
  const perWave = Math.ceil(targetCount / waves);
  const waveSpan = (timeLimitSeconds * 0.85) / waves;
  const topBandHeight = Math.max(0, rangeHeight * RANGE_GEOMETRY.spawnTopBand - targetRadius * 2);
  for (let index = 0; index < targetCount; index += 1) {
    const wave = Math.min(waves - 1, Math.floor(index / perWave));
    const indexInWave = index - wave * perWave;
    const countThisWave = Math.min(perWave, targetCount - wave * perWave);
    attackers.push({
      index,
      wave,
      spawnSeconds: Number((wave * waveSpan + (indexInWave / countThisWave) * waveSpan + random() * (waveSpan * 0.1 / countThisWave)).toFixed(3)),
      totalTimeSeconds: Number(Math.max(1.2, targetLifetimeSeconds * (1 - 0.15 * wave)).toFixed(3)),
      x: targetRadius + random() * (rangeWidth - targetRadius * 2),
      y: targetRadius + random() * topBandHeight,
    });
  }
  return attackers.sort((a, b) => a.spawnSeconds - b.spawnSeconds);
}

/** The fixed point every attacker heads toward: the defender's position. */
export function defenderPoint(spec: TargetRangeSpec): { readonly x: number; readonly y: number } {
  return { x: spec.params.rangeWidth / 2, y: spec.params.rangeHeight - RANGE_GEOMETRY.defenderYInset };
}

function distance(ax: number, ay: number, bx: number, by: number): number { return Math.hypot(ax - bx, ay - by); }

/**
 * Seconds until a shot fired at spawn time (aimed at the attacker's spawn
 * position, the same fixed point the whole way -- the compiled game's shot
 * keeps flying in a straight line, it doesn't home) meets the attacker.
 * Both are moving toward each other along the same straight line (the shot
 * travels from the defender toward the attacker's spawn point; the
 * attacker travels from that same point back toward the defender), so this
 * is an exact 1D head-on closing-speed calculation -- they always meet,
 * regardless of which one is faster, because they're closing distance
 * from both ends at once. Verified by sweep across the full validator
 * range that this always resolves before the attacker's own travel time
 * (`totalTimeSeconds`) and leaves enough margin for the compiled game's
 * discrete 60fps collision check to actually register it (worst case
 * ~11.5px of clearance against the 48px minimum hit window).
 */
function meetTimeSeconds(attacker: SpawnedAttacker, defender: { readonly x: number; readonly y: number }, projectileSpeed: number): number {
  const dist = distance(attacker.x, attacker.y, defender.x, defender.y);
  const attackerSpeed = dist / attacker.totalTimeSeconds;
  return dist / (attackerSpeed + projectileSpeed);
}

/**
 * Proves winnability by construction: firing at spawn time toward each
 * attacker's spawn point always physically meets it (see meetTimeSeconds)
 * as long as the meeting still happens before the round's time limit.
 * Counts how many of the seeded attackers clear that bar for the shot
 * trace, then separately proves the defense itself survives: simulating
 * "shoot every attacker immediately at spawn" against the full schedule up
 * to the winning hit, any attacker that DOESN'T clear the time-limit bar
 * breaches and costs a protectee -- that must stay under the starting
 * protectee count, or the stakes system itself would be unwinnable by
 * construction. Also budgets the presentation load: peak concurrent
 * attackers plus the fixed particle cap must fit the Atom-tier frame budget.
 */
export function evaluateTargetRange(spec: TargetRangeSpec): GameplayEvaluationReceipt {
  const { projectileSpeed, goalHits, timeLimitSeconds } = spec.params;
  const defender = defenderPoint(spec);
  const attackers = spawnSchedule(spec);

  const trace: { readonly elapsedSeconds: number; readonly action: { readonly type: "shoot"; readonly x: number; readonly y: number } }[] = [];
  let hits = 0;
  let livesRemaining = RANGE_GEOMETRY.protecteeCount;
  let breachesBeforeGoal = 0;
  for (const attacker of attackers) {
    if (hits >= goalHits) break;
    const meet = meetTimeSeconds(attacker, defender, projectileSpeed);
    const hittable = attacker.spawnSeconds + meet <= timeLimitSeconds;
    if (hittable) {
      hits += 1;
      trace.push({ elapsedSeconds: Number(attacker.spawnSeconds.toFixed(3)), action: { type: "shoot", x: Number(attacker.x.toFixed(2)), y: Number(attacker.y.toFixed(2)) } });
    } else {
      livesRemaining -= 1;
      breachesBeforeGoal += 1;
    }
  }

  let peakConcurrent = 0;
  for (const attacker of attackers) {
    let live = 0;
    for (const other of attackers) {
      if (other.spawnSeconds <= attacker.spawnSeconds && other.spawnSeconds + other.totalTimeSeconds > attacker.spawnSeconds) live += 1;
    }
    peakConcurrent = Math.max(peakConcurrent, live);
  }
  const presentationOps = peakConcurrent * 3 + PARTICLE_CAP + 30;

  const checks: EvaluationCheck[] = [
    { id: "touch-target", passed: spec.params.targetRadius >= 24, evidence: `attacker hit radius ${spec.params.targetRadius}px` },
    { id: "targets-reachable", passed: hits >= goalHits, evidence: `${hits}/${goalHits} required attackers are interceptable before they reach the defender, on the seeded schedule` },
    { id: "protectees-survive", passed: breachesBeforeGoal < RANGE_GEOMETRY.protecteeCount, evidence: `${breachesBeforeGoal} of ${RANGE_GEOMETRY.protecteeCount} protectees lost under optimal play while reaching the goal` },
    { id: "presentation-budget", passed: presentationOps <= 400, evidence: `${peakConcurrent} peak concurrent attackers + ${PARTICLE_CAP} pooled particles = ${presentationOps} frame ops` },
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace,
    metrics: { reachableHits: hits, totalTargets: attackers.length, peakConcurrent, waves: spec.params.waves ?? 1, livesRemaining: Math.max(0, livesRemaining) },
  };
}
